import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { AppDispatch, RootState } from '../index';
import { getAuthActions } from '@/lib/auth/authActions';
import { isTokenExpiringSoon } from '@/lib/auth/tokenExpiry';
import { setToken } from '../slices/authSlice';
import { requestInteractiveLogin } from '@/lib/auth/interactiveLogin';

type ApiBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

class TokenRefreshCoordinator {
  private refreshPromise: Promise<string | null> | null = null;

  async refresh(dispatch: AppDispatch): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.acquireFreshToken(dispatch);

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async acquireFreshToken(dispatch: AppDispatch): Promise<string | null> {
    const actions = getAuthActions();
    if (!actions) return null;

    try {
      const token = await actions.getAccessToken();
      if (!token) return null;

      dispatch(setToken(token));
      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }
}

function getAuthToken(api: BaseQueryApi): string | null {
  return (api.getState() as RootState).auth.token;
}

function shouldRefreshBeforeRequest(api: BaseQueryApi): boolean {
  return isTokenExpiringSoon(getAuthToken(api));
}

function isAuthFailure(error: FetchBaseQueryError | undefined): boolean {
  return error?.status === 401;
}

function isFetchFailureWithStaleToken(
  error: FetchBaseQueryError | undefined,
  api: BaseQueryApi,
): boolean {
  return error?.status === 'FETCH_ERROR' && isTokenExpiringSoon(getAuthToken(api));
}

function requestLoginAfterRefreshFailure(): void {
  requestInteractiveLogin({ reason: 'unauthorized' });
}

const sharedTokenRefresh = new TokenRefreshCoordinator();

export function withTokenReauth(rawBaseQuery: ApiBaseQuery): ApiBaseQuery {
  return async (args, api, extraOptions) => {
    let refreshedBeforeRequest = false;

    if (shouldRefreshBeforeRequest(api)) {
      refreshedBeforeRequest = true;
      await sharedTokenRefresh.refresh(api.dispatch as AppDispatch);
    }

    let result = await rawBaseQuery(args, api, extraOptions);

    if (isAuthFailure(result.error)) {
      const newToken = await sharedTokenRefresh.refresh(api.dispatch as AppDispatch);

      if (newToken) {
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        requestLoginAfterRefreshFailure();
      }
    } else if (!refreshedBeforeRequest && isFetchFailureWithStaleToken(result.error, api)) {
      const newToken = await sharedTokenRefresh.refresh(api.dispatch as AppDispatch);

      if (newToken) {
        result = await rawBaseQuery(args, api, extraOptions);
      }
    }

    return result;
  };
}
